import React, { useState, useEffect } from 'react';
import { ArrowLeft, Sparkles, BookOpen, Heart, Sunset, Compass, RefreshCcw } from 'lucide-react';
import { WisdomShlok } from '../types';
import ShlokCard from '../components/ShlokCard';
import { fetchWisdomShloks } from '../services/wisdomService';
import logger from "@/utils/logger";

interface ManasAidProps {
  onBack?: () => void;
  activeContext?: {
    id: string;
    title: string;
    detail?: string;
  };
}

const ShlokSkeleton = () => (
  <div className="h-full bg-white rounded-3xl border border-indigo-100/30 p-8 flex flex-col animate-pulse">
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-slate-100 rounded-2xl"></div>
        <div className="space-y-2">
          <div className="h-4 w-24 bg-slate-100 rounded"></div>
          <div className="h-2 w-16 bg-slate-50 rounded"></div>
        </div>
      </div>
      <div className="h-8 w-8 bg-slate-50 rounded"></div>
    </div>
    <div className="flex-1 space-y-4">
      <div className="h-12 w-full bg-slate-50 rounded-2xl"></div>
      <div className="h-8 w-3/4 mx-auto bg-slate-50/50 rounded-xl"></div>
      <div className="h-16 w-full bg-slate-50 rounded-2xl"></div>
    </div>
    <div className="mt-8 pt-8 border-t border-gray-50 space-y-4">
      <div className="h-3 w-32 bg-slate-100 rounded"></div>
      <div className="h-20 w-full bg-slate-50 rounded-2xl"></div>
    </div>
  </div>
);

const ManasAid: React.FC<ManasAidProps> = ({ onBack, activeContext }) => {
  const [shloks, setShloks] = useState<WisdomShlok[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadShloks = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Use detail from context if provided as the search query
      const query = activeContext?.detail || activeContext?.title || "";
      const data = await fetchWisdomShloks(query);
      setShloks(data);
    } catch (err) {
      logger.error("[ManasAid] Failed to load shloks", err);
      setError("Wisdom is loading — please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    loadShloks();
  }, [activeContext]);

  const isContextual = !!activeContext;

  return (
    <div className="h-full flex flex-col bg-slate-50 overflow-y-auto">
      {/* Standardized Header */}
      <div className="bg-[#F0F0FF] border-b-2 border-[#4C3B8A] p-6 flex flex-col md:flex-row justify-between gap-4 items-center sticky top-0 z-20">
        <div className="flex items-center gap-4">
          {onBack && (
            <button onClick={onBack} className="flex items-center gap-2 p-2 rounded-xl text-[#6B5FAD] hover:text-[#4C3B8A] hover:bg-[#F0F0FF] transition-colors">
              <Compass className="rotate-180" size={20} />
              <span className="text-sm font-bold uppercase tracking-widest">Back</span>
            </button>
          )}
          <div className="w-12 h-12 bg-[#4C3B8A] rounded-2xl flex items-center justify-center text-white shadow-sm">
            <Sunset size={24} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-serif text-2xl font-bold text-[#4C3B8A]">Manas Aid</h1>
            </div>
            <p className="text-[#6B5FAD] text-xs font-medium uppercase tracking-[0.2em] mt-0.5">The Wisdom Engine</p>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-12 md:py-20 pb-32">
        <div className="mb-16 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <h2 className="text-3xl font-serif font-black text-[#4C3B8A] mb-3">
              {isContextual ? "Wisdom for Your Journey" : "Daily Wisdom Repository"}
            </h2>
            <p className="text-gray-500 font-medium">
              {isContextual 
                ? `Selected shloks for your current state regarding ${activeContext?.title.toLowerCase()}.` 
                : "Selected shloks for mental resilience and spiritual grounding."}
            </p>
          </div>
          {!isLoading && !error && shloks.length > 0 && (
            <div className="flex items-center gap-3 px-6 py-4 bg-white rounded-3xl shadow-sm border border-[#4C3B8A]/10">
              <Sparkles className="text-indigo-400" size={18} />
              <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                Showing {shloks.length} Wisdom {shloks.length === 1 ? 'Entry' : 'Entries'}
              </span>
            </div>
          )}
        </div>

        {error ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mb-6">
              <RefreshCcw size={24} />
            </div>
            <p className="text-gray-600 font-medium mb-8">{error}</p>
            <button 
              onClick={loadShloks}
              className="px-8 py-3 bg-[#4C3B8A] text-white rounded-2xl font-bold text-sm uppercase tracking-widest hover:bg-[#3B2D70] transition-colors shadow-lg shadow-indigo-100"
            >
              Retry Loading
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {isLoading ? (
              <>
                <ShlokSkeleton />
                <ShlokSkeleton />
                <ShlokSkeleton />
              </>
            ) : shloks.length === 0 ? (
              <div className="col-span-full flex flex-col items-center justify-center py-20 text-center opacity-60">
                <BookOpen size={48} className="text-[#4C3B8A] mb-4 opacity-20" />
                <p className="text-[#4C3B8A] font-serif text-lg">The scrolls are quiet today.</p>
                <p className="text-xs text-gray-400 mt-1 uppercase tracking-widest">Unable to retrieve wisdom at this moment.</p>
              </div>
            ) : (
              shloks.map((shlok) => (
                <ShlokCard key={shlok.id} shlok={shlok} />
              ))
            )}
            
            {/* Contribution Placeholder */}
            {!isLoading && shloks.length > 0 && shloks.length < 3 && (
              <div className="bg-gradient-to-br from-indigo-50 to-white rounded-3xl border border-dashed border-[#4C3B8A]/20 flex flex-col items-center justify-center p-12 text-center group hover:border-[#4C3B8A]/40 transition-all duration-500">
                <div className="w-16 h-16 rounded-full bg-white shadow-sm flex items-center justify-center text-indigo-400 mb-6 group-hover:scale-110 transition-transform duration-500">
                  <Heart size={24} />
                </div>
                <h4 className="font-serif font-bold text-[#4C3B8A] mb-2">Expanding the Scrolls</h4>
                <p className="text-xs text-gray-400 font-medium leading-relaxed">
                  More shloks are being carefully translated and verified by our Vaidyas. Check back soon for more wisdom.
                </p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Footer Branding */}
      <footer className="p-8 border-t border-gray-100 bg-white text-center">
         <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
           Educational Guidance based on the Pancha Kosha Framework
         </p>
      </footer>
    </div>
  );
};

export default ManasAid;
